import React from "react";
import {
  getAchievementHtmlItems,
  getExperienceHighlightHtmlItems,
  htmlToRichListItems,
  ResumeRichText,
  ResumeRichTextInline,
} from "./resumeRichText.jsx";

export {
  getAchievementHtmlItems,
  getExperienceHighlightHtmlItems,
  htmlToRichListItems,
  ResumeRichText,
  ResumeRichTextInline,
};

export function hasText(value) {
  return typeof value === "string" && value.trim().length > 0;
}

const PLACEHOLDER_VALUE_REGEX = /^(?:test|n\/a|na|null|undefined|sdfghj\w*|jhgjh\w*|asdfg?h?|qwerty|lorem\s+ipsum|\[object\s+object\])$/i;
const INVALID_TECHNOLOGY_TOKEN_REGEX = /^(?:test|n\/a|na|null|undefined|sdfghj\w*|jhgjh\w*|asdfg?h?|qwerty|lorem\s+ipsum|\[object\s+object\]|madhava|madhav|tatikonda)$/i;
const LOCATION_TECH_LEAK_REGEX = /\b(?:websocket|react(?:\.js)?|node(?:\.js)?|web3(?:\.js)?|data visualization|charts?|graphs?|redux|axios|rest api|javascript|typescript|mongodb|sql|cron jobs?)\b/i;
const FORBIDDEN_RENDER_TEXT_REGEX = /(?:i actually think this is a better product model|here's a refined codex prompt|resume pipeline v1\.0 rc|do not introduce unrelated features|what i'd ask codex|overall assessment|codex task|users want to explore templates|before they decide to pay|i'd recommend changing the flow|new subscription flow|everyone can\s+(?:upload|parse|edit|switch|preview)|subscription check\s*↓\s*subscribed)/i;
const LIST_DISPLAY_KEYS = [
  "text",
  "title",
  "description",
  "details",
  "skills",
  "summary",
  "name",
  "label",
  "value",
  "achievement",
  "item",
];
const COMMON_BROKEN_WORD_RUNS = new Set([
  "senior",
  "react",
  "developer",
  "developers",
  "multiple",
  "software",
  "engineer",
  "engineers",
  "frontend",
  "backend",
  "fullstack",
  "with",
  "years",
  "experience",
  "experienced",
  "professional",
  "summary",
  "profile",
  "visualization",
  "services",
]);

export function stripHtml(value = "") {
  if (!hasText(value)) return "";

  return value
    .replace(/<li[^>]*>/gi, "\n")
    .replace(/<\/li>/gi, "")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+\n/g, "\n")
    .replace(/\n{2,}/g, "\n")
    .replace(/[ \t]{2,}/g, " ")
    .trim();
}

function sanitizeRenderableValue(value = "") {
  if (value && typeof value === "object") return "";

  return repairBrokenWordRuns(
    String(value || "")
    .replace(/\u00a0/g, " ")
    .replace(/\bbackendservices\b/gi, "backend services")
    .replace(/\bperformanceacross\b/gi, "performance across")
    .replace(/\b(Built|using|Developed|Implemented|Designed|Created|Delivered)-(?=[A-Za-z])/gi, "$1 ")
    .replace(/\breusable-UI\b/gi, "reusable UI")
    .replace(/^(?:\+\s*){2,}/, "+ ")
    .replace(/^(?:[-*•]\s*){2,}/, "• ")
    .replace(/\s+/g, " ")
    .trim()
  );
}

function repairBrokenWordRuns(value = "") {
  const tokens = String(value || "").split(/\s+/).filter(Boolean);
  if (tokens.length < 2) return String(value || "").trim();

  const repaired = [];

  for (let index = 0; index < tokens.length; index += 1) {
    const first = tokens[index];
    const second = tokens[index + 1];
    const third = tokens[index + 2];

    const twoPart = `${first || ""}${second || ""}`.toLowerCase();
    const threePart = `${first || ""}${second || ""}${third || ""}`.toLowerCase();

    if (
      third &&
      /^[a-z]+$/i.test(first) &&
      /^[a-z]+$/i.test(second) &&
      /^[a-z]+$/i.test(third) &&
      COMMON_BROKEN_WORD_RUNS.has(threePart)
    ) {
      repaired.push(threePart);
      index += 2;
      continue;
    }

    if (
      second &&
      /^[a-z]+$/i.test(first) &&
      /^[a-z]+$/i.test(second) &&
      COMMON_BROKEN_WORD_RUNS.has(twoPart)
    ) {
      repaired.push(twoPart);
      index += 1;
      continue;
    }

    repaired.push(first);
  }

  return repaired.join(" ").trim();
}

function hasRenderableValue(value) {
  const normalized = sanitizeRenderableValue(value);
  return normalized.length > 0 &&
    !PLACEHOLDER_VALUE_REGEX.test(normalized) &&
    !FORBIDDEN_RENDER_TEXT_REGEX.test(normalized);
}

function parseExperienceYearsValue(value) {
  if (value == null) return null;

  const normalized = String(value).replace(/,/g, ".").trim();
  const match = normalized.match(/\d+(?:\.\d+)?/);
  if (!match) return null;

  const parsedValue = Number.parseFloat(match[0]);
  return Number.isFinite(parsedValue) ? parsedValue : null;
}

export function hasMoreThanFourYearsExperience(data) {
  const experienceValues = [
    data?.totalExperience,
    data?.relevantExperience,
    data?.experienceYears,
    data?.yearsOfExperience,
  ];

  return experienceValues.some((value) => {
    const parsedValue = parseExperienceYearsValue(value);
    return Number.isFinite(parsedValue) && parsedValue > 4;
  });
}

function firstRenderableValue(...values) {
  for (const value of values) {
    const normalized = sanitizeRenderableValue(value);
    if (hasRenderableValue(normalized)) return normalized;
  }

  return "";
}

function isRenderableTechnologyValue(value) {
  const normalized = sanitizeRenderableValue(value);
  if (!normalized || INVALID_TECHNOLOGY_TOKEN_REGEX.test(normalized)) {
    return false;
  }

  if (/[@]/.test(normalized)) return false;
  if (/^\+?\d[\d\s()+-]{5,}$/.test(normalized)) return false;

  return hasRenderableValue(normalized);
}

function isRenderableLocationValue(value) {
  const normalized = sanitizeRenderableValue(value);
  if (!hasRenderableValue(normalized)) return false;
  if (normalized.length > 45) return false;
  if (/[0-9@]/.test(normalized)) return false;
  if (LOCATION_TECH_LEAK_REGEX.test(normalized)) return false;
  if (normalized.split(/\s+/).length > 5) return false;
  if (!/^[A-Za-z][A-Za-z\s,.-]*$/.test(normalized)) return false;
  return true;
}

function collectRenderableListValues(value) {
  if (Array.isArray(value)) {
    return value
      .flatMap((item) => {
        if (typeof item === "string") return sanitizeRenderableValue(item);
        if (item && typeof item === "object") return collectRenderableListValues(getObjectListDisplayValue(item));
        return "";
      })
      .filter(hasRenderableValue);
  }

  if (value && typeof value === "object") {
    return collectRenderableListValues(getObjectListDisplayValue(value));
  }

  if (hasRenderableValue(value)) {
    return String(value)
      .split(/,|\n|\||•/g)
      .map((item) => sanitizeRenderableValue(item))
      .filter(hasRenderableValue);
  }

  return [];
}

function getObjectListDisplayValue(value) {
  if (!value || typeof value !== "object") return "";

  for (const key of LIST_DISPLAY_KEYS) {
    const candidate = value[key];
    if (typeof candidate === "string" && hasRenderableValue(candidate)) {
      return candidate;
    }
    if (Array.isArray(candidate)) {
      const items = collectRenderableListValues(candidate);
      if (items.length > 0) return items.join("\n");
    }
  }

  return "";
}

function escapeRenderableHtml(value = "") {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function collectFirstRenderableListValue(sources = []) {
  for (const source of sources) {
    const values = collectRenderableListValues(source);
    if (values.length > 0) return values;
  }

  return [];
}

const MONTH_LABELS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

function getLastDayOfMonth(year, monthIndex) {
  return new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();
}

function getMonthIndex(value = "") {
  const normalized = value.toLowerCase().slice(0, 3);
  return MONTH_LABELS.findIndex((month) => month.toLowerCase() === normalized);
}

function buildDateRank(year, monthIndex = 11, isRangeEnd = false) {
  const day = isRangeEnd ? getLastDayOfMonth(year, monthIndex) : 1;
  return Date.UTC(year, monthIndex, day);
}

function parseSingleRenderableDate(value, { isRangeEnd = false } = {}) {
  const normalized = sanitizeRenderableValue(value);
  if (!normalized) return null;
  if (/^(?:present|current|now)$/i.test(normalized)) {
    return { rank: Number.MAX_SAFE_INTEGER, label: "Present" };
  }

  const monthYearMatch = normalized.match(
    /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Sept|Oct|Nov|Dec)[a-z]*\s+(\d{4})$/i,
  );
  if (monthYearMatch) {
    const monthIndex = getMonthIndex(monthYearMatch[1]);
    const year = Number(monthYearMatch[2]);
    if (monthIndex !== -1) {
      return {
        rank: buildDateRank(year, monthIndex, isRangeEnd),
        label: `${MONTH_LABELS[monthIndex]} ${year}`,
      };
    }
  }

  const isoDateMatch = normalized.match(/^((?:19|20)\d{2})-(\d{1,2})-(\d{1,2})$/);
  if (isoDateMatch) {
    const year = Number(isoDateMatch[1]);
    const monthIndex = Number(isoDateMatch[2]) - 1;
    if (monthIndex >= 0 && monthIndex <= 11) {
      return {
        rank: buildDateRank(year, monthIndex, isRangeEnd),
        label: `${MONTH_LABELS[monthIndex]} ${year}`,
      };
    }
  }

  const numericDateMatch = normalized.match(/^(\d{1,2})[/-](\d{1,2})[/-]((?:19|20)\d{2})$/);
  if (numericDateMatch) {
    const monthIndex = Number(numericDateMatch[2]) - 1;
    const year = Number(numericDateMatch[3]);
    if (monthIndex >= 0 && monthIndex <= 11) {
      return {
        rank: buildDateRank(year, monthIndex, isRangeEnd),
        label: `${MONTH_LABELS[monthIndex]} ${year}`,
      };
    }
  }

  const numericMonthYearMatch = normalized.match(/^(\d{1,2})[/-]((?:19|20)\d{2})$/);
  if (numericMonthYearMatch) {
    const monthIndex = Number(numericMonthYearMatch[1]) - 1;
    const year = Number(numericMonthYearMatch[2]);
    if (monthIndex >= 0 && monthIndex <= 11) {
      return {
        rank: buildDateRank(year, monthIndex, isRangeEnd),
        label: `${MONTH_LABELS[monthIndex]} ${year}`,
      };
    }
  }

  const yearMatch = normalized.match(/^(19|20)\d{2}$/);
  if (yearMatch) {
    const year = Number(yearMatch[0]);
    return {
      rank: buildDateRank(year, isRangeEnd ? 11 : 0, isRangeEnd),
      label: String(year),
    };
  }

  return null;
}

function splitDateRangeValue(value) {
  const normalized = sanitizeRenderableValue(value);
  if (!normalized) return [];

  return normalized
    .split(/\s+(?:-|–|—|to)\s+/i)
    .map((part) => sanitizeRenderableValue(part))
    .filter(Boolean);
}

function parseRenderableDateValue(value, preference = "end") {
  const parts = splitDateRangeValue(value);

  if (parts.length > 1) {
    const target = preference === "start" ? parts[0] : parts[parts.length - 1];
    const parsedRangePart = parseSingleRenderableDate(target, {
      isRangeEnd: preference !== "start",
    });
    if (parsedRangePart) return parsedRangePart.rank;
  }

  const parsed = parseSingleRenderableDate(value, {
    isRangeEnd: preference !== "start",
  });
  return parsed ? parsed.rank : Number.NEGATIVE_INFINITY;
}

function formatRenderableDate(value, options) {
  const parsed = parseSingleRenderableDate(value, options);
  return parsed?.label || "";
}

function formatRenderableDateRangeValue(value) {
  const parts = splitDateRangeValue(value);

  if (parts.length > 1) {
    const first = formatRenderableDate(parts[0]);
    const last = formatRenderableDate(parts[parts.length - 1], { isRangeEnd: true });

    if (first && last) return `${first} - ${last}`;
  }

  return formatRenderableDate(value) || sanitizeRenderableValue(value);
}

function getExperienceRole(experience = {}) {
  return firstRenderableValue(
    experience.role,
    experience.title,
    experience.jobTitle,
    experience.jobRole,
    experience.designation,
    experience.position,
    experience.positionTitle,
    experience.roleTitle,
  );
}

function getExperienceCompany(experience = {}) {
  return firstRenderableValue(
    experience.company,
    experience.companyName,
    experience.employer,
    experience.employerName,
    experience.organization,
    experience.organizationName,
    experience.organisation,
    experience.organisationName,
    experience.workplace,
    experience.firm,
    experience.business,
  );
}

function getExperienceStartDate(experience = {}) {
  return firstRenderableValue(
    experience.startDate,
    experience.from,
    experience.start,
    experience.dates?.startDate,
    experience.dates?.from,
  );
}

function getExperienceEndDate(experience = {}) {
  return firstRenderableValue(
    experience.endDate,
    experience.to,
    experience.end,
    experience.dates?.endDate,
    experience.dates?.to,
  );
}

function getExperienceYears(experience = {}) {
  return firstRenderableValue(
    experience.years,
    experience.duration,
    experience.period,
    experience.dateRange,
    experience.date,
  );
}

function getExperienceLocation(experience = {}) {
  return firstRenderableValue(
    experience.location,
    experience.city,
    experience.place,
  );
}

function isCurrentExperience(experience = {}) {
  if (experience.isCurrent === true || experience.current === true) return true;

  const status = sanitizeRenderableValue(
    experience.status || experience.endDate || experience.to || experience.end || experience.years || experience.duration,
  ).toLowerCase();

  return /\b(present|current|now|ongoing)\b/.test(status);
}

function resolveExperienceSortRank(experience = {}) {
  const endRank = experience.isCurrent === true
    ? Number.MAX_SAFE_INTEGER
    : parseRenderableDateValue(experience.endDate || experience.years, "end");
  const startRank = parseRenderableDateValue(experience.startDate || experience.years, "start");
  return { endRank, startRank };
}

function cleanExperienceBulletText(value, experience = {}) {
  const bullet = sanitizeRenderableValue(stripHtml(value));
  if (!hasRenderableValue(bullet)) return "";

  if (/^\s*(?:technologies?|environment)\s*:/i.test(bullet)) {
    const technologyValues = bullet
      .replace(/^\s*(?:technologies?|environment)\s*:\s*/i, "")
      .split(/,|\/|\||;|•/g)
      .map((part) => sanitizeRenderableValue(part))
      .filter(isRenderableTechnologyValue);

    if (technologyValues.length === 0) {
      return "";
    }
  }

  const role = getExperienceRole(experience);
  const company = getExperienceCompany(experience);
  const project = sanitizeRenderableValue(experience.project);
  const years = sanitizeRenderableValue(
    formatDateRange(experience.startDate, experience.endDate, experience.years),
  );
  const normalizedBullet = bullet.toLowerCase();

  if (
    normalizedBullet === role.toLowerCase() ||
    normalizedBullet === company.toLowerCase() ||
    normalizedBullet === project.toLowerCase() ||
    normalizedBullet === years.toLowerCase()
  ) {
    return "";
  }

  if (
    role &&
    company &&
    normalizedBullet === `${role} ${company}`.toLowerCase()
  ) {
    return "";
  }

  return bullet;
}

function buildDescriptionTextLines(description = "") {
  return String(description || "")
    .replace(/\u00a0/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(?:li|p|div|section|article|h1|h2|h3|h4|h5|h6)>/gi, "\n")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+\n/g, "\n")
    .replace(/\n{2,}/g, "\n")
    .split(/\n+/)
    .map((line) => sanitizeRenderableValue(line))
    .filter(Boolean);
}

function looksLikeTechnologyContinuationText(value = "") {
  const normalized = sanitizeRenderableValue(value);
  if (!normalized) return false;
  if (/[.]{1}\s*$/.test(normalized)) return false;
  if (/\b(developed|implemented|worked|designed|led|collaborated|built|created|optimized|ensuring|using)\b/i.test(normalized)) {
    return false;
  }

  const tokens = normalized
    .split(/,|\/|\||;|•/g)
    .map((token) => sanitizeRenderableValue(token))
    .filter(Boolean);

  if (tokens.length < 2) return false;
  if (tokens.some((token) => token.split(/\s+/).length > 4)) return false;

  return true;
}

function extractInlineExperienceTechnologies(experience = {}) {
  const explicitValues = collectFirstRenderableListValue([
    experience?.technologies,
    experience?.technology,
    experience?.skills,
    experience?.skill,
    experience?.techStack,
    experience?.tools,
  ]);

  const descriptionItems = htmlToRichListItems(
    experience?.description || "",
    Number.MAX_SAFE_INTEGER,
  );
  const descriptionTextLines = buildDescriptionTextLines(experience?.description || "");
  const rawLines = [
    ...descriptionItems,
    ...descriptionTextLines,
  ].filter(Boolean);

  const sequentialInlineValues = [];
  for (let index = 0; index < rawLines.length; index += 1) {
    const text = sanitizeRenderableValue(stripHtml(rawLines[index]));
    const technologyMatch = text.match(/\b(?:technologies?|environment)\s*:\s*(.*)$/i);
    if (!technologyMatch) continue;

    if (hasRenderableValue(technologyMatch[1])) {
      sequentialInlineValues.push(...technologyMatch[1]
        .split(/,|\/|\||;|•/g)
        .map((part) => sanitizeRenderableValue(part))
        .filter(isRenderableTechnologyValue));
      continue;
    }

    let continuationIndex = index + 1;
    while (continuationIndex < rawLines.length) {
      const continuationLine = sanitizeRenderableValue(stripHtml(rawLines[continuationIndex]));
      if (!looksLikeTechnologyContinuationText(continuationLine)) break;

      sequentialInlineValues.push(...continuationLine
        .split(/,|\/|\||;|•/g)
        .map((part) => sanitizeRenderableValue(part))
        .filter(isRenderableTechnologyValue));
      continuationIndex += 1;
    }
  }

  const inlineValues = rawLines.flatMap((item) => {
    const text = sanitizeRenderableValue(stripHtml(item));
    const technologyMatch = text.match(/\b(?:technologies?|environment)\s*:\s*(.+)$/i);
    if (!technologyMatch) return [];

    return technologyMatch[1]
      .split(/,|\/|\||;|•/g)
      .map((part) => sanitizeRenderableValue(part))
      .filter(isRenderableTechnologyValue);
  });

  // if (
  //   process.env.NODE_ENV !== "production" &&
  //   typeof window !== "undefined" &&
  //   rawLines.some((item) => /\b(?:technologies?|environment)\s*:/i.test(String(item || "")))
  // ) {
  //   console.debug("cvcanvas:experience-technologies", {
  //     company: experience?.company || "",
  //     role: experience?.role || "",
  //     explicitValues,
  //     descriptionTextLines,
  //     sequentialInlineValues,
  //     inlineValues,
  //     rawLines,
  //     rawDescription: experience?.description || "",
  //   });
  // }

  const seen = new Set();

  return [...explicitValues, ...sequentialInlineValues, ...inlineValues].filter((item) => {
    const key = sanitizeRenderableValue(item).toLowerCase();
    if (!key || !isRenderableTechnologyValue(item) || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function htmlToBullets(value = "", limit = Number.MAX_SAFE_INTEGER) {
  void limit;

  if (!hasText(value)) return [];

  const listMatches = Array.from(
    value.matchAll(/<li[^>]*>([\s\S]*?)<\/li>/gi)
  ).map((match) => stripHtml(match[1])).filter(Boolean);

  if (listMatches.length > 0) {
    return listMatches;
  }

  return stripHtml(value)
    .split(/\n|(?:\.\s+)/)
    .map((item) => item.trim())
    .filter(Boolean);
}

export function formatDateRange(startDate, endDate, years) {
  const explicitParts = [startDate, endDate]
    .map((part, index) =>
      formatRenderableDate(part, { isRangeEnd: index === 1 }) ||
      sanitizeRenderableValue(part),
    )
    .filter(hasRenderableValue);

  if (explicitParts.length > 0) {
    return explicitParts.join(" - ");
  }

  if (hasRenderableValue(years)) {
    return formatRenderableDateRangeValue(years);
  }

  return "";
}

export function isExperienceFilled(experience) {
  if (!experience || typeof experience !== "object") return false;

  return [
    getExperienceCompany(experience),
    getExperienceRole(experience),
    experience.project,
    getExperienceStartDate(experience),
    getExperienceEndDate(experience),
    getExperienceYears(experience),
    stripHtml(experience.description),
    stripHtml(experience.responsibilities),
    stripHtml(experience.summary),
  ].some((value) => hasRenderableValue(value));
}

export function isEducationFilled(education) {
  if (!education || typeof education !== "object") return false;

  return [
    education.degree,
    education.field,
    education.institution,
    education.organization,
    education.school,
    education.schoolName,
    education.college,
    education.collegeName,
    education.university,
    education.universityName,
    education.board,
    education.name,
    education.course,
    education.qualification,
    education.title,
    education.accreditation?.education,
    education.from,
    education.to,
    education.startDate,
    education.endDate,
    education.dates?.startDate,
    education.dates?.endDate,
    education.startYear,
    education.endYear,
    education.passingYear,
    education.year,
    education.years,
    education.duration,
    education.date,
    education.score,
    education.grade?.value,
    education.grade,
    education.gpa,
    education.cgpa,
    education.percentage,
  ].some((value) => hasRenderableValue(value));
}

function getExperienceSourceItems(data) {
  const sourceKeys = [
    "experience",
    "experiences",
    "workExperience",
    "workExperiences",
    "employmentHistory",
    "professionalExperience",
    "professionalExperiences",
    "careerExperience",
    "careerExperiences",
    "workHistory",
    "employment",
    "jobs",
    "positions",
  ];

  for (const key of sourceKeys) {
    if (Array.isArray(data?.[key]) && data[key].some(isExperienceFilled)) {
      return data[key];
    }
  }

  return [];
}

export function getActiveExperiences(data) {
  return getExperienceSourceItems(data)
    .filter(isExperienceFilled)
    .map((experience, index) => {
      const current = isCurrentExperience(experience);
      const endDate = getExperienceEndDate(experience);

      return {
        ...experience,
        isCurrent: current,
        company: getExperienceCompany(experience),
        role: getExperienceRole(experience),
        title: getExperienceRole(experience),
        project: firstRenderableValue(experience.project, experience.projectName),
        location: getExperienceLocation(experience),
        startDate: getExperienceStartDate(experience),
        endDate: current && !hasRenderableValue(endDate)
          ? "Present"
          : endDate,
        years: getExperienceYears(experience),
        description: firstRenderableValue(
          experience.description,
          experience.responsibilities,
          experience.summary,
          experience.details,
        ),
        __renderIndex: index,
      };
    })
    .sort((left, right) => {
      const leftRank = resolveExperienceSortRank(left);
      const rightRank = resolveExperienceSortRank(right);

      if (rightRank.endRank !== leftRank.endRank) {
        return rightRank.endRank - leftRank.endRank;
      }

      if (rightRank.startRank !== leftRank.startRank) {
        return rightRank.startRank - leftRank.startRank;
      }

      return left.__renderIndex - right.__renderIndex;
    })
    .map(({ __renderIndex, ...experience }) => experience);
}

export function getActiveEducation(data) {
  return Array.isArray(data?.education)
    ? data.education
      .filter(isEducationFilled)
      .map((education) => ({
        ...education,
        institution: firstRenderableValue(
          education.institution ||
            education.organization,
          education.university,
          education.college,
          education.school,
          education.schoolName,
          education.collegeName,
          education.universityName,
          education.board,
          education.name,
          education.accreditation?.organization,
        ),
        degree: firstRenderableValue(
          education.degree ||
            education.course,
          education.qualification,
          education.title,
          education.accreditation?.education,
          education.accreditation?.name,
          education.description,
        ),
        field: firstRenderableValue(
          education.field,
          education.major,
          education.specialization,
          education.area,
          education.studyField,
        ),
        from: firstRenderableValue(
          education.from,
          education.startDate,
          education.dates?.startDate,
          education.startYear,
        ),
        to: firstRenderableValue(
          education.to,
          education.endDate,
          education.dates?.endDate,
          education.endYear,
          education.passingYear,
        ),
        years: firstRenderableValue(
          education.years,
          education.year,
          education.duration,
          education.date,
        ),
        score: firstRenderableValue(
          education.score,
          education.grade?.value,
          education.gpa,
          education.cgpa,
          education.percentage,
        ),
      }))
    : [];
}

export function getSkills(data, limit) {
  const groupedSkills = collectRenderableListValues(data?.skillGroups);

  const primarySkills = collectRenderableListValues(groupedSkills);
  const fallbackSkills =
    primarySkills.length > 0
      ? []
      : collectFirstRenderableListValue([
          data?.skills,
          data?.skill,
          data?.technicalSkills?.skills,
          data?.technicalSkills?.items,
          data?.technicalSkills?.values,
          data?.technicalSkills,
          data?.technologies,
          data?.tools,
        ]);

  const seen = new Set();
  const normalizedLimit =
    Number.isFinite(limit) && limit > 0 ? limit : Number.MAX_SAFE_INTEGER;

  return [...primarySkills, ...fallbackSkills]
    .filter((skill) => {
      const key = sanitizeRenderableValue(skill).toLowerCase();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, normalizedLimit);
}

export function getLanguages(data, limit = Number.MAX_SAFE_INTEGER) {
  void limit;

  return Array.isArray(data?.languages)
    ? data.languages
      .map((language) => sanitizeRenderableValue(language))
      .filter(hasRenderableValue)
    : [];
}

export function getAchievements(data, limit = Number.MAX_SAFE_INTEGER) {
  const htmlItems = getAchievementHtmlItems(data, Number.MAX_SAFE_INTEGER);
  const fallbackItems = htmlItems.length
    ? []
    : collectFirstRenderableListValue([data?.achievements]).map(escapeRenderableHtml);

  const cleanedItems = [...htmlItems, ...fallbackItems]
    .map((item) => ({
      html: item,
      text: sanitizeRenderableValue(stripHtml(item)),
    }))
    .filter((item) => hasRenderableValue(item.text))
    .filter(
      (item, index, collection) =>
        collection.findIndex(
          (candidate) => candidate.text.toLowerCase() === item.text.toLowerCase(),
        ) === index,
    )
    .slice(0, limit);

  return cleanedItems.map((item, index) =>
    React.createElement(ResumeRichTextInline, {
      html: item.html,
      dataField: "achievements",
      key: `achievement-${index}`,
    }),
  );
}

export function getProjects(data, limit = 4) {
  const explicitProjects = Array.isArray(data?.projects)
    ? data.projects
        .map((project) => {
          const details = htmlToRichListItems(
            firstRenderableValue(
              project.description,
              project.details,
              project.summary,
            ),
          )
            .map((detail) => ({
              html: detail,
              text: sanitizeRenderableValue(stripHtml(detail)),
            }))
            .filter((detail) => hasRenderableValue(detail.text))
            .map((detail) => detail.html);

          return {
            ...project,
            title: firstRenderableValue(
              project.title,
              project.name,
              project.project,
            ),
            subtitle: [
              firstRenderableValue(project.role, project.company),
              formatDateRange(
                project.startDate,
                project.endDate,
                project.years,
              ),
            ]
              .filter(hasRenderableValue)
              .join(" . "),
            details,
          };
        })
        .filter((project) => hasRenderableValue(project.title))
    : [];

  if (explicitProjects.length > 0) {
    return explicitProjects.slice(0, limit);
  }

  return [];
}

export function getSummaryText(data) {
  const summary = getAboutText(data);
  if (hasRenderableValue(summary)) return summary;

  const objective = getObjectiveText(data);
  return hasRenderableValue(objective) ? objective : "";
}

export function getAboutText(data) {
  return firstRenderableValue(
    data?.summary,
    data?.aboutMe,
    data?.about,
    data?.professionalSummary,
    data?.profileSummary,
    data?.profile,
  );
}

export function getObjectiveText(data) {
  if (hasMoreThanFourYearsExperience(data)) {
    return "";
  }

  return firstRenderableValue(
    data?.objective,
    data?.careerObjective,
    data?.career_objective,
    data?.careerSummary,
    data?.careerGoal,
    data?.goal,
  );
}

export function getInitials(name) {
  const parts = (name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2);

  if (parts.length === 0) return "";

  return parts.map((part) => part[0].toUpperCase()).join("");
}

export function splitDisplayName(name) {
  const parts = (name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (parts.length === 0) {
    return {
      primary: "",
      secondary: "",
    };
  }

  if (parts.length === 1) {
    return {
      primary: parts[0],
      secondary: "",
    };
  }

  return {
    primary: parts.slice(0, -1).join(" "),
    secondary: parts[parts.length - 1],
  };
}

export function getContactItems(data) {
  return [
    {
      key: "location",
      value: data?.location,
    },
    {
      key: "phone",
      value: data?.phone,
    },
    {
      key: "email",
      value: data?.email,
    },
    {
      key: "linkedin",
      value: data?.linkedin,
    },
    {
      key: "github",
      value: data?.github,
    },
  ]
    .map((item) => ({
      ...item,
      value: sanitizeRenderableValue(item.value),
    }))
    .map((item) =>
      item.key === "location" && !isRenderableLocationValue(item.value)
        ? { ...item, value: "" }
        : item,
    )
    .filter((item) => hasRenderableValue(item.value));
}

export function getPrimaryContactItems(data) {
  return [
    {
      key: "location",
      value: data?.location,
    },
    {
      key: "phone",
      value: data?.phone,
    },
    {
      key: "email",
      value: data?.email,
    },
  ]
    .map((item) => ({
      ...item,
      value: sanitizeRenderableValue(item.value),
    }))
    .map((item) =>
      item.key === "location" && !isRenderableLocationValue(item.value)
        ? { ...item, value: "" }
        : item,
    )
    .filter((item) => hasRenderableValue(item.value));
}

export function getSocialItems(data) {
  return [
    {
      key: "linkedin",
      value: data?.linkedin,
    },
    {
      key: "github",
      value: data?.github,
    },
  ]
    .map((item) => ({
      ...item,
      value: sanitizeRenderableValue(item.value),
    }))
    .filter((item) => hasRenderableValue(item.value));
}

export function normalizeExternalUrl(value) {
  if (!hasText(value)) return "";

  const trimmed = value.trim();

  if (/^(?:https?:\/\/|mailto:|tel:)/i.test(trimmed)) {
    return trimmed;
  }

  return `https://${trimmed}`;
}

export function getExperienceHighlights(experience, limit = Number.MAX_SAFE_INTEGER) {
  void limit;

  const cleanedItems = getExperienceHighlightHtmlItems(
    experience,
    Number.MAX_SAFE_INTEGER,
  )
    .map((item) => ({
      html: item,
      text: cleanExperienceBulletText(item, experience),
    }))
    .filter((item) => hasRenderableValue(item.text))
    .filter(
      (item, index, collection) =>
        collection.findIndex(
          (candidate) => candidate.text.toLowerCase() === item.text.toLowerCase(),
        ) === index,
    );

  return cleanedItems.map((item, index) =>
    React.createElement(ResumeRichTextInline, {
      html: item.html,
      dataField: "description",
      key: `experience-highlight-${index}`,
    }),
  );
}

export function getExperienceTechnologies(experience) {
  return extractInlineExperienceTechnologies(experience);
}

function parseColor(color) {
  if (typeof color !== "string") return null;

  const trimmed = color.trim();

  const hexMatch = trimmed.match(/^#([0-9a-f]{3,8})$/i);
  if (hexMatch) {
    let hex = hexMatch[1];

    if (hex.length === 3 || hex.length === 4) {
      hex = hex
        .split("")
        .map((char) => char + char)
        .join("");
    }

    if (hex.length === 6 || hex.length === 8) {
      return {
        r: parseInt(hex.slice(0, 2), 16),
        g: parseInt(hex.slice(2, 4), 16),
        b: parseInt(hex.slice(4, 6), 16),
      };
    }
  }

  const rgbMatch = trimmed.match(
    /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})/i
  );

  if (rgbMatch) {
    return {
      r: Number(rgbMatch[1]),
      g: Number(rgbMatch[2]),
      b: Number(rgbMatch[3]),
    };
  }

  return null;
}

function clampChannel(value) {
  return Math.min(255, Math.max(0, Math.round(value)));
}

export function withAlpha(color, alpha) {
  const parsed = parseColor(color);

  if (!parsed) return color;

  return `rgba(${parsed.r}, ${parsed.g}, ${parsed.b}, ${alpha})`;
}

export function mixColors(colorA, colorB, weight = 0.5) {
  const first = parseColor(colorA);
  const second = parseColor(colorB);

  if (!first || !second) {
    return colorA;
  }

  const ratio = Math.min(1, Math.max(0, weight));

  return `rgb(${clampChannel(first.r * ratio + second.r * (1 - ratio))}, ${clampChannel(first.g * ratio + second.g * (1 - ratio))}, ${clampChannel(first.b * ratio + second.b * (1 - ratio))})`;
}

export function getContrastTextColor(
  backgroundColor,
  darkColor = "#0f172a",
  lightColor = "#ffffff"
) {
  const parsed = parseColor(backgroundColor);

  if (!parsed) {
    return lightColor;
  }

  const brightness =
    (parsed.r * 299 + parsed.g * 587 + parsed.b * 114) / 1000;

  return brightness >= 160 ? darkColor : lightColor;
}
